import { Container, Message, MessageList, renderers } from '@botpress/webchat'
import { initialConfig, LatestWebchatTheme } from '@bpinternal/shared'
import {
  RadioGroup,
  Slider,
  Button,
  Separator,
  AsyncButton,
  TooltipProvider,
  TooltipTrigger,
  Tooltip,
  TooltipContent,
} from '@bpinternal/ui-kit-next'
import allFonts from 'google-fonts-complete/api-response.json'
import { useMemo } from 'react'
import { RadioCard } from '~/elements/RadioCard'
import { HexColorInput } from '~/features/webchat/components/elements/HexColorInput'
import { getUseConfigStore } from '~/features/webchat/stores/useConfigStore'
import { useLocalStore } from '~/stores'
import WebchatComponentPreview from '../elements/WebchatComponentPreview'
import { cn } from '~/utils'
import { FontCombobox } from '../elements/FontCombobox'
import { Section } from './Section'
import { SubSection } from './SubSection'
import { RotateCcw } from 'lucide-react'
import useNavigationBlocker, { navigationBlockerOptions } from '../../hooks/useNavigationBlocker'
import { DEFAULT_CUSTOM_STYLESHEET } from '../../utils/const'

export type FontMeta = {
  family: string
  category?: string
}

type Props = {
  workspaceId: string
  botId: string
  title?: string
  description?: string
}

export default function Appearance(props: Props) {
  const { workspaceId, botId } = props
  const useConfigStore = useMemo(() => getUseConfigStore({ botId, workspaceId }), [botId, workspaceId])

  const color = useConfigStore((state) => state.color)
  const themeMode = useConfigStore((state) => state.themeMode)
  const variant = useConfigStore((state) => state.variant)
  const headerVariant = useConfigStore((state) => state.headerVariant)
  const fontFamily = useConfigStore((state) => state.fontFamily)
  const radius = useConfigStore((state) => state.radius)
  const customStylesheet = useConfigStore((state) => state.customStyleSheet)
  const setThemeMode = useConfigStore((state) => state.setThemeMode)
  const setVariant = useConfigStore((state) => state.setVariant)
  const setColor = useConfigStore((state) => state.setColor)
  const setFont = useConfigStore((state) => state.setFontFamily)
  const setRadius = useConfigStore((state) => state.setRadius)
  const setHeaderVariant = useConfigStore((state) => state.setHeaderVariant)

  const dashboardTheme = useLocalStore((state) => state.getPreferredTheme())

  const saveConfig = useConfigStore((state) => state.saveConfig)
  const _updating = useConfigStore((state) => state._updating)
  const _originalConfig = useConfigStore((state) => state._originalConfig)

  const fonts: FontMeta[] = Object.entries(allFonts).map(([, meta]) => ({
    family: meta.family,
    category: meta.category,
  }))

  const webchatTheme: LatestWebchatTheme = {
    themeMode: dashboardTheme,
    headerVariant,
    variant,
    color,
    radius,
  }

  const hasPendingChanges =
    color !== _originalConfig?.color ||
    themeMode !== _originalConfig?.themeMode ||
    variant !== _originalConfig?.variant ||
    radius !== _originalConfig?.radius ||
    fontFamily !== _originalConfig?.fontFamily ||
    headerVariant !== _originalConfig?.headerVariant ||
    (customStylesheet ?? DEFAULT_CUSTOM_STYLESHEET) !== (_originalConfig?.customStyleSheet ?? DEFAULT_CUSTOM_STYLESHEET)

  useNavigationBlocker(hasPendingChanges as boolean, {
    ...navigationBlockerOptions,
    confirmCallback: async () => {
      try {
        await saveConfig()
        return true
      } catch (error) {
        console.log('Failed to save changes', error)
        return false
      }
    },
    cancelCallback: async () => {
      if (_originalConfig) {
        setColor(_originalConfig.color)
        setThemeMode(_originalConfig.themeMode)
        setVariant(_originalConfig.variant)
        setRadius(_originalConfig.radius)
        setFont(_originalConfig.fontFamily)
        setHeaderVariant(_originalConfig.headerVariant)
      }
      return false
    },
  })

  return (
    <Section
      name="Bot Appearance"
      actionButton={
        <AsyncButton
          className="hidden h-9 lg:block"
          onClick={saveConfig}
          disabled={_updating || !hasPendingChanges}
          loading={_updating}
        >
          Publish Changes
        </AsyncButton>
      }
      sticky
    >
      <SubSection title="Primary Color" description="Choose the primary brand color for your webchat interface.">
        <div className="flex gap-2 lg:flex-row lg:justify-end">
          <div className="relative">
            <HexColorInput
              className="!h-9 !w-28 rounded-lg bg-neutral-200 text-sm font-medium"
              value={color}
              onChange={setColor}
            />
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline" className="h-9 w-9" onClick={() => setColor(initialConfig.color)}>
                  <RotateCcw className="h-4 w-4 font-extrabold" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Reset to the default color</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </SubSection>
      <Separator className="my-10" />
      <SubSection title="Font" description="Select the primary font family for your interface.">
        <div className="flex w-full flex-col gap-2 lg:flex-row lg:justify-end">
          <FontCombobox fonts={fonts} fontFamily={fontFamily} setFont={setFont} />
        </div>
      </SubSection>
      <Separator className="my-10" />
      <SubSection title="Theme Mode" description="Choose between light and dark appearance modes.">
        <RadioGroup
          className="flex w-full lg:justify-end"
          onValueChange={(value: 'light' | 'dark') => setThemeMode(value)}
          value={themeMode}
        >
          <RadioCard
            id="light-theme"
            value="light"
            className="flex w-full lg:min-w-[110px] xl:max-w-56"
            labelClassName="lg:px-2"
          >
            <ThemeModePreview theme={{ ...webchatTheme, themeMode: 'light' }} />
          </RadioCard>
          <RadioCard
            id="dark-theme"
            value="dark"
            className="flex w-full lg:min-w-[110px] xl:max-w-56"
            labelClassName="lg:px-2"
          >
            <ThemeModePreview theme={{ ...webchatTheme, themeMode: 'dark' }} />
          </RadioCard>
        </RadioGroup>
      </SubSection>
      <Separator className="my-10" />
      <SubSection title="Header Style" description="Chose the chat header appearance and styling.">
        <RadioGroup
          className="flex w-full lg:justify-end"
          onValueChange={(value: 'glass' | 'solid') => setHeaderVariant(value)}
          value={headerVariant}
        >
          <RadioCard
            value="glass"
            id="glass-header"
            className="flex w-full lg:min-w-[110px] xl:max-w-56"
            labelClassName="px-4"
          >
            <HeaderPreview theme={{ ...webchatTheme, headerVariant: 'glass' }} />
          </RadioCard>
          <RadioCard
            value="solid"
            id="solid-header"
            className="flex w-full lg:min-w-[110px] xl:max-w-56"
            labelClassName="px-4"
          >
            <HeaderPreview theme={{ ...webchatTheme, headerVariant: 'solid' }} />
          </RadioCard>
        </RadioGroup>
      </SubSection>
      <Separator className="my-10" />
      <SubSection title="Message Styling" description="Customize the appearance of chat messages.">
        <div className="flex w-full flex-col xl:flex-row">
          <RadioGroup
            className="flex w-full flex-row lg:justify-end"
            onValueChange={(value: 'soft' | 'solid') => setVariant(value)}
            value={variant}
          >
            <RadioCard
              value="soft"
              id="soft-message"
              className="flex w-full lg:min-w-[110px] xl:max-w-56"
              labelClassName="px-4"
            >
              <MessageListPreview theme={{ ...webchatTheme, variant: 'soft' }} />
            </RadioCard>
            <RadioCard
              value="solid"
              id="solid-message"
              className="flex w-full lg:min-w-[110px] xl:max-w-56"
              labelClassName="px-4"
            >
              <MessageListPreview theme={{ ...webchatTheme, variant: 'solid' }} />
            </RadioCard>
          </RadioGroup>
        </div>
      </SubSection>
      <Separator className="my-10" />
      <SubSection
        className="flex flex-col md:flex-row"
        title="Corner Radius"
        description="Adjust the roundness of interface elements and components."
      >
        <div className="flex w-full flex-col">
          <div className="flex justify-between pb-4">
            <span className="text-sm text-muted-foreground">Sharp</span>
            <span className="text-sm text-muted-foreground">Round</span>
          </div>
          <Slider
            className="w-full"
            value={[radius ?? 1]}
            onValueChange={(value: number[]) => {
              setRadius(value[0] ?? 1)
            }}
            min={0.5}
            max={4}
            step={0.1}
          />
        </div>
      </SubSection>
    </Section>
  )
}

const ThemeModePreview = ({ theme }: { theme: LatestWebchatTheme }) => {
  const messages = [
    {
      direction: 'outgoing' as const,
      sender: { name: 'User' },
      id: 'incoming-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: '',
        },
      },
    },
    {
      direction: 'incoming' as const,
      sender: { name: 'Bot' },
      id: 'outgoing-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: '',
        },
      },
    },
  ]
  const isDarkMode = useLocalStore((state) => state.getPreferredTheme()) === 'dark'

  const getPreviewColor = () => {
    if (theme.themeMode === 'dark' && isDarkMode) {
      return 'bg-neutral-200'
    }

    if (theme.themeMode === 'dark' && !isDarkMode) {
      return 'bg-neutral-700'
    }

    if (theme.themeMode === 'light' && !isDarkMode) {
      return 'bg-neutral-200'
    }

    if (theme.themeMode === 'light' && isDarkMode) {
      return 'bg-neutral-700'
    }
  }

  return (
    <WebchatComponentPreview className="w-full max-w-[98%] bg-transparent px-4 pt-4 sm:px-0" theme={theme}>
      <Container className="!h-[100px] w-full !rounded-b-none rounded-t-lg !border-b-0 bg-neutral-50">
        <div
          className={cn('flex items-center gap-1 px-2 py-2 transition-all duration-300', {
            'bg-[var(--bpPrimary-600)]': theme.headerVariant === 'solid',
          })}
        >
          <div className={cn('aspect-square h-[14px] rounded-full', getPreviewColor())}></div>
          <div className="flex w-full flex-col gap-2">
            <div className={cn('h-1 w-6 rounded-full', getPreviewColor())}></div>
          </div>
        </div>
        <MessageList
          className={cn('!pb-0 lg:!w-full')}
          messages={messages}
          showMarquee={false}
          showDateSystemMessage={false}
          showMessageStatus={false}
          showIncomingMessageAvatar={false}
        />
      </Container>
    </WebchatComponentPreview>
  )
}

function HeaderPreview({ theme }: { theme: LatestWebchatTheme }) {
  return (
    <WebchatComponentPreview className="pointer-events-none !h-[80px] !min-w-16 bg-transparent pt-4" theme={theme}>
      <Container className="!h-[72px] !rounded-b-none rounded-t-lg !border-b-0 bg-white">
        <div
          className={cn('flex items-center px-2 py-2 transition-all duration-300', {
            'bg-[var(--bpPrimary-600)]': theme.headerVariant === 'solid',
          })}
        >
          <div className="flex items-center gap-1">
            <div className="flex aspect-square h-5 items-center justify-center rounded-full bg-neutral-200 text-xs">
              B
            </div>
            <div className="h-4 w-6 text-xs text-[var(--header-title)]">Bot</div>
          </div>
        </div>
      </Container>
    </WebchatComponentPreview>
  )
}

const MessageListPreview = ({ theme }: { theme: LatestWebchatTheme }) => {
  const messages = [
    {
      direction: 'incoming' as const,
      sender: { name: 'Bot' },
      id: 'incoming-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: 'Hi',
        },
      },
    },
    {
      direction: 'outgoing' as const,
      sender: { name: 'User' },
      id: 'outgoing-message',
      timestamp: new Date(),
      block: {
        type: 'bubble' as const,
        block: {
          type: 'text' as const,
          text: 'Hey',
        },
      },
    },
  ]

  return (
    <WebchatComponentPreview className="!h-[100px] w-full items-start bg-transparent" theme={theme}>
      {messages.map((message) => (
        <div key={message.id} className="min-w-11 bg-neutral-50 px-2 py-2.5 [&_*]:!text-xs">
          <Message key={message.id} renderers={renderers} showAvatar={false} {...message} />
        </div>
      ))}
    </WebchatComponentPreview>
  )
}
